import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  ValidationStyle,
  InputList,
  validationEngineFromInputInformationList,
  MessagePopupCore,
  objectAtPath,
} from '@utahdts/dts-react-common';
import PropTypes from 'prop-types';
import useRefLazy from '../misc/hooks/useRefLazy';
import companyInputs from './inputs/companyInputs';
import webservice from '../app/webservice/webservice';
import userInputs from './inputs/userInputs';
import roles from '../app/roles/roles';

const propTypes = {};

const Registration = () => {
  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState(undefined);
  const [nextPage, setNextPage] = useState(false);
  const [inputValues, setInputValues] = useState({
    id: null,
    name: null,
    fein: null,
    address: null,
    address2: null,
    city: null,
    state: null,
    zip: null,
  });
  const inputValuesRef = useRef();
  inputValuesRef.current = inputValues;

  const [validationEngineStyle, setValidationEngineStyle] = useState(
    ValidationStyle.BLURRED,
  );
  const validationEngineRef = useRef(undefined);

  const formatFeinInput = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    const prefix = digits.slice(0, 2);
    const suffix = digits.slice(2, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 2) return prefix;
    return `${prefix}-${suffix}`;
  };

  const inputs = useRefLazy(() =>
    companyInputs(
      {
        // Modify this callback
        inputsOnChange: (field, value) => {
          if (field === 'fein') {
            // If the field is 'fein', format the value
            const formattedFein = formatFeinInput(value);
            setInputValues((oldValues) => ({
              ...oldValues,
              [field]: formattedFein,
            }));
          } else {
            // For all other fields, update as before
            setInputValues((oldValues) => ({ ...oldValues, [field]: value }));
          }
        },
      },
      true,
    ),
  );

  const [validationEngine, setValidationEngine] = useState(undefined);
  validationEngineRef.current = validationEngine;
  if (validationEngine) {
    validationEngine.validationStyle = validationEngineStyle;
  }

  const checkCompany = () => {
    setValidationEngineStyle(ValidationStyle.ALWAYS);
    if (
      validationEngineRef.current &&
      validationEngineRef.current.isValid(inputValuesRef.current)
    ) {
      setNextPage(true);
    }
  };

  const [contactValues, setContactValues] = useState({
    name: null,
    email: null,
    phone: null,
  });
  const contactValuesRef = useRef();
  contactValuesRef.current = contactValues;

  const [contactValidationStyle, setContactValidationStyle] = useState(
    ValidationStyle.BLURRED,
  );
  const contactValidationRef = useRef(undefined);

  const contactInputs = useRefLazy(() =>
    userInputs(
      {
        inputsOnChange: (field, value) =>
          setContactValues((oldValues) => ({ ...oldValues, [field]: value })),
      },
      true,
      true,
    ),
  );

  const [contactValidation, setContactValidation] = useState(undefined);
  contactValidationRef.current = contactValidation;
  if (contactValidation) {
    contactValidation.validationStyle = contactValidationStyle;
  }

  const [alternateValues, setAlternateValues] = useState({
    name: null,
    email: null,
    phone: null,
  });
  const alternateValuesRef = useRef();
  alternateValuesRef.current = alternateValues;

  const [alternateValidationStyle, setAlternateValidationStyle] = useState(
    ValidationStyle.BLURRED,
  );
  const alternateValidationRef = useRef(undefined);

  const alternateInputs = useRefLazy(() =>
    userInputs(
      {
        inputsOnChange: (field, value) =>
          setAlternateValues((oldValues) => ({ ...oldValues, [field]: value })),
      },
      true,
      false,
    ),
  );

  const [alternateValidation, setAlternateValidation] = useState(undefined);
  alternateValidationRef.current = alternateValidation;
  if (alternateValidation) {
    alternateValidation.validationStyle = alternateValidationStyle;
  }

  const submit = () => {
    setContactValidationStyle(ValidationStyle.ALWAYS);
    setAlternateValidationStyle(ValidationStyle.ALWAYS);
    // Primary and alternate contacts must be different people.
    const primaryEmail = (contactValuesRef.current?.email || '')
      .trim()
      .toLowerCase();
    const alternateEmail = (alternateValuesRef.current?.email || '')
      .trim()
      .toLowerCase();
    if (alternateEmail && alternateEmail === primaryEmail) {
      MessagePopupCore.addMessage({
        title: 'Duplicate contact email',
        message:
          'The primary and alternate contacts must use different email addresses.',
      });
      return;
    }
    if (
      contactValidationRef.current &&
      contactValidationRef.current.isValid(contactValuesRef.current) &&
      alternateValidationRef.current.isValid(alternateValuesRef.current)
    ) {
      setBusy(true);
      console.log(
        '[REGISTRATION] Submit initiated. Calling webservice.company.submit...',
      ); // 1. Log initiation

      webservice.company
        .submit(inputValues)
        .then((data) => {
          // ... your existing .then() logic ...
          console.log(
            '[REGISTRATION] webservice.company.submit resolved. Data:',
            JSON.stringify(data, null, 2),
          );

          const companyErrors = objectAtPath(data, 'data.errors');
          console.log(
            '[REGISTRATION] Extracted companyErrors:',
            JSON.stringify(companyErrors, null, 2),
          );

          if (companyErrors && companyErrors.length > 0) {
            const firstErrorTitle = companyErrors[0]?.title;
            console.log(
              '[REGISTRATION] First error title from payload:',
              firstErrorTitle,
            );

            if (firstErrorTitle === 'Webservice Communication Interruption') {
              console.log(
                '[REGISTRATION] "Webservice Communication Interruption" detected in resolved payload. Should only show webservice dialog.',
              );
              setBusy(false);
              return;
            } else {
              console.log(
                '[REGISTRATION] Other error in resolved payload. Calling displayCompanyError().',
              );
              displayCompanyError(); // Handles errors within a resolved promise
              return;
            }
          }

          // ... (rest of your .then() logic for successful company submission and user submissions)
          if (data && data.submitOrg) {
            console.log(
              '[REGISTRATION] Company submission successful in payload. Proceeding with user submissions.',
            );
            const org = data.submitOrg;
            const userPromises = [];
            let primary = setUser(contactValues, org, true);
            userPromises.push(webservice.user.submit(primary));

            if (alternateValues.email) {
              // Only submit alternate if email exists
              let alternative = setUser(alternateValues, org, false);
              userPromises.push(webservice.user.submit(alternative));
            }

            Promise.all(userPromises)
              .then(() => {
                console.log('[REGISTRATION] User submissions successful.');
                displaySuccess(); // This function calls setBusy(false)
              })
              .catch((userError) => {
                console.error(
                  '[REGISTRATION] Error during user submission:',
                  userError,
                );

                let errorMessage = '';
                if (typeof userError === 'string') {
                  errorMessage = userError;
                } else if (
                  userError &&
                  userError.message &&
                  typeof userError.message === 'string'
                ) {
                  errorMessage = userError.message;
                }

                // Check if the error message indicates "No organization information was found"
                if (
                  errorMessage.includes('No organization information was found')
                ) {
                  console.log(
                    '[Registration.jsx] "No organization information was found" error (rejected user submission). Assuming webservice.js handled the dialog.',
                  );
                  setBusy(false); // Ensure busy state is cleared
                  // Do NOT call displayUserError() for this specific, handled rejection
                  return;
                }
                // Add more 'else if' blocks here for other specific user submission errors
                // that webservice.js might handle.

                // For any other unexpected user submission rejection, display the generic user error.
                console.log(
                  '[Registration.jsx] Unhandled user submission rejection type. Calling displayUserError(). Actual error message:',
                  errorMessage,
                );
                displayUserError(); // This function calls setBusy(false)
              });
          } else {
            // ... (your existing else block for company submission issues)
            console.log(
              '[REGISTRATION] No companyErrors in payload, but also no data.submitOrg. Unexpected success response. Calling displayCompanyError().',
            );
            console.error(
              '[REGISTRATION] Unexpected success response from company submission (no submitOrg):',
              data,
            );
            displayCompanyError();
          }
        })
        .catch((err) => {
          // This .catch() handles:
          // - Actual rejections from any of the promises in the `promises` array
          //   (e.g., network error during contact save or company save).
          // - Synchronous errors thrown within the .then() block above.
          console.error(
            '[Registration.jsx] Error during company save operation (Promise rejected):',
            err,
          );

          let errorMessage = '';
          if (typeof err === 'string') {
            errorMessage = err;
          } else if (err && err.message && typeof err.message === 'string') {
            errorMessage = err.message;
          }

          // Check for specific error messages that might have been handled by the webservice
          if (
            errorMessage.includes('Organization with FEIN') &&
            errorMessage.includes('already exists')
          ) {
            console.log(
              '[Registration.jsx] Duplicate FEIN error (rejected promise). Assuming webservice.js handled the dialog.',
            );
            setBusy(false); // Ensure busy state is cleared
            // Do NOT call displayCompanyError() for this specific, handled rejection
            return;
          }
          // Add more 'else if' blocks here for other specific rejected errors
          // that webservice.js might handle and for which you don't want to show a generic dialog.
          // Example:
          // else if (errorMessage.includes('Another specific error message from webservice')) {
          //     console.log('[Registration.jsx] Another specific error (rejected promise). Webservice handled dialog.');
          //     setBusy(false);
          //     return;
          // }

          // For any other unexpected rejection, or if err.message is not specific enough,
          // display the generic company error.
          console.log(
            '[Registration.jsx] Unhandled rejection type or non-specific error message. Calling displayCompanyError(). Actual error message:',
            errorMessage,
          );
          displayCompanyError(); // This function calls setBusy(false)
        });
    }
  };

  const setUser = (inputs, org, isPrimary) => {
    return {
      name: inputs.name,
      email: inputs.email,
      phone: inputs.phone,
      orgs: [org.id],
      submitOrg: org.id,
      roles: [
        {
          role: roles.orgAdmin,
          org: org.id,
          primaryContact: isPrimary,
          alternativeContact: !isPrimary,
        },
      ],
    };
  };

  const displaySuccess = () => {
    setBusy(false);
    MessagePopupCore.addMessage({
      title: 'Registration successful',
      message: (
        <p>
          The company has been registered. <br />
          The Utah Insurance Department will notify you once the company has
          been reviewed and approved.
        </p>
      ),
    });
    setNextPage(false);
    setContactValues({
      name: null,
      email: null,
      phone: null,
    });
    setInputValues({
      id: null,
      name: null,
      fein: null,
      address: null,
      address2: null,
      city: null,
      state: null,
      zip: null,
    });
  };

  const displayCompanyError = () => {
    setBusy(false);
    MessagePopupCore.addMessage({
      title: 'Something went wrong',
      message:
        'An error occurred while registering the company. Please try again later or contact UID at uid.healthresearch@utah.gov.',
    });
  };

  const displayUserError = () => {
    setBusy(false);
    MessagePopupCore.addMessage({
      title: 'Something went wrong',
      message:
        'An error occurred while registering users. Please try again later or contact UID at uid.healthresearch@utah.gov.',
    });
  };

  useEffect(() => {
    setValidationEngine(
      validationEngineFromInputInformationList(inputs.current),
    );
    setContactValidation(
      validationEngineFromInputInformationList(contactInputs.current),
    );
    setAlternateValidation(
      validationEngineFromInputInformationList(alternateInputs.current),
    );
  }, [inputs.current]);

  return (
    <div className="registration--container">
      <h3>Registration</h3>
      <p className="mb-zero text-standard">
        In order to access the system, a company needs to be registered. Please
        fill out the form below.
        <br />
        The Utah Department of Insurance will then validate your account.
      </p>
      <div className={nextPage ? 'hidden' : ''}>
        <h4 className="mb-spacing">Company Info</h4>
        <InputList
          inputs={Object.values(inputs.current)}
          data={inputValues}
          validationEngine={validationEngine}
          className="stack-form flex flex-wrap half-form"
        />
        <Button label="Next" busy={isBusy} onClick={checkCompany} />
      </div>
      <div className={nextPage ? '' : 'hidden'}>
        <h4 className="mb-spacing">Primary Contact</h4>
        <InputList
          inputs={Object.values(contactInputs.current)}
          data={contactValues}
          validationEngine={contactValidation}
          className="stack-form flex flex-wrap half-form"
        />
        <h4 className="mb-spacing">Alternate Contact</h4>
        <InputList
          inputs={Object.values(alternateInputs.current)}
          data={alternateValues}
          validationEngine={alternateValidation}
          className="stack-form flex flex-wrap half-form"
        />
        <Button
          label="Back"
          busy={isBusy}
          onClick={() => setNextPage(false)}
          className="button--black mr-spacing--large"
        />
        <Button label="Submit" busy={isBusy} onClick={submit} />
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
};

Registration.propTypes = propTypes;

export default Registration;
